import ReactQuill from './quill';
import React from 'react';
import type Quill from 'quill';
import { fieldSchema, FieldType } from './types';
import { agreementStyles } from './styles';
import { Box } from '@startuptools/ui/Box';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { useEffect, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { SetField } from './EditFields';
import { templateToDocument } from './render-document';
import { z } from 'zod';
import { Trans } from '@lingui/react';

export const DocumentPreview = ({
  template,
  fields,
  setValue,
  previewMode = false,
  documentStatus,
  cancelSignature,
}: {
  cancelSignature: () => Promise<unknown>;
  template: Parameters<typeof templateToDocument>[0];
  fields: z.infer<typeof fieldSchema>[];
  setValue: (value: { fieldId: string; value: unknown }) => Promise<unknown>;
  previewMode?: boolean;
  documentStatus: 'Pending' | 'Created' | 'Signing' | 'Completed';
}) => {
  const ref = React.useRef<Quill>(null);
  const [showLockDialog, setShowLockDialog] = useState<'hidden' | 'shown' | 'dismissed'>('hidden');

  const renderedDocument = React.useMemo(() => templateToDocument(template, fields), [template, fields]);

  const [tooltipAnchor, setTooltipAnchor] = useState<{ fieldId: string; elementId: string } | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  useEffect(() => {
    const quillContainer = document.getElementById('quill-container');

    const clickHandler = (event: Event) => {
      if (!['Pending', 'Created'].includes(documentStatus)) {
        setShowLockDialog('shown');
        return;
      }
      const target = event.currentTarget as HTMLElement;
      const fieldId = target.getAttribute('data-field-id');
      const elementId = target.getAttribute('id');
      if (!fieldId || !elementId) throw new Error('Field id is not defined');
      setTooltipAnchor({ fieldId, elementId });
      event.stopPropagation();
    };
    if (quillContainer) {
      observerRef.current = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if (node instanceof HTMLElement && node.classList.contains('field')) {
                node.addEventListener('click', clickHandler);
              }
            });
            mutation.removedNodes.forEach(node => {
              if (node instanceof HTMLElement && node.classList.contains('field')) {
                node.removeEventListener('click', clickHandler);
              }
            });
          }
        });
      });

      observerRef.current.observe(quillContainer, { childList: true, subtree: true });

      const existingElements = quillContainer.getElementsByClassName('field');
      Array.from(existingElements).forEach(element => {
        element.addEventListener('click', clickHandler);
      });
    }

    return () => {
      const quillContainer = document.getElementById('quill-container');
      if (!quillContainer) return;
      const existingElements = quillContainer.getElementsByClassName('field');
      Array.from(existingElements).forEach(element => {
        element.removeEventListener('click', clickHandler);
      });
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [documentStatus]);

  const booleans = fields.filter(v => v.type === FieldType.BOOLEAN);

  return (
    <Stack gap={2}>
      {showLockDialog === 'shown' && documentStatus === 'Completed' && (
        <ConfirmDialog
          header={<Trans id="The document has already been signed." />}
          onCancel={() => setShowLockDialog('hidden')}
          confirmProps={{ children: <Trans id="Cancel" /> }}
          onConfirm={() => setShowLockDialog('hidden')}
        >
          <Trans id="You can't make changes to a signed document." />
        </ConfirmDialog>
      )}
      {showLockDialog === 'shown' && documentStatus === 'Signing' && (
        <ConfirmDialog
          header={<Trans id="The signature process is ongoing." />}
          onCancel={() => setShowLockDialog('dismissed')}
          confirmProps={{ children: <Trans id="Yes" /> }}
          onConfirm={async () => {
            await cancelSignature();
            setShowLockDialog('dismissed');
          }}
        >
          <Trans id="If you want to make changes, you will need to cancel the signature process. Do you want to continue?" />
        </ConfirmDialog>
      )}
      {booleans.length ? (
        <Card>
          <Stack gap={1}>
            <Typography level="title-md">Settings</Typography>
            <Box>
              {fields
                .filter(v => v.type === FieldType.BOOLEAN)
                .map(v => (
                  <SetField
                    previewMode={previewMode}
                    key={v.id}
                    field={v}
                    onChange={data => setValue({ fieldId: v.id, value: data.value })}
                  />
                ))}
            </Box>
          </Stack>
        </Card>
      ) : null}

      <Box sx={agreementStyles}>
        <ClickAwayListener
          onClickAway={() => {
            setTooltipAnchor(null);
          }}
        >
          <Popper open={Boolean(tooltipAnchor)} anchorEl={() => document.getElementById(tooltipAnchor!.elementId)!}>
            <Card>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: 320,
                  justifyContent: 'center',
                  p: 1,
                }}
              >
                {tooltipAnchor && (
                  <SetField
                    previewMode={previewMode}
                    field={fields.find(v => v.id === tooltipAnchor.fieldId)}
                    onChange={async data => {
                      await setValue({ fieldId: tooltipAnchor.fieldId, value: data.value });
                      setTooltipAnchor(null);
                    }}
                  />
                )}
              </Box>
            </Card>
          </Popper>
        </ClickAwayListener>

        <ReactQuill
          readOnly
          ref={ref}
          value={renderedDocument}
          theme="snow"
          modules={{
            toolbar: false,
          }}
        />
      </Box>
    </Stack>
  );
};
